import { Stack, SvgIcon } from "@mui/material";
import { neutral } from "@flat-ui";
import { withEllipsisThreeLines } from "@styles/common-styles";
import { ElementType } from "react";
import { useDynamicHeight } from "@hooks/use-dynamic-height";
import { SphereTooltip } from "@components/common/sphere-tooltip";

interface Props {
  /** The icon to display on the first row of the page */
  icon: ElementType;

  /** The title to display on the second row of the page */
  title: string;

  /** The subtitle to display on the third row of the page */
  subtitle: string | JSX.Element;

  /** The button to display on the bottom of page */
  button?: React.ReactNode;
}

/** Defines the size of the icon in pixels */
const ICON_SIZE = "61px";

/** Since the colors or this page are custom, store them here to modify them easily */
const pageColors = {
  /** The background color of the whole empty page */
  pageBackground: neutral[50],

  /** The color of the title text */
  title: neutral[800],

  /** The color of the subtitle text */
  subtitle: neutral[600],
};

/**
 * Shows an empty page with an icon, title and subtitle.
 */
export function EmptyPage({
  title,
  subtitle,
  icon,
  button,
}: Props): JSX.Element {
  const { ref, height } = useDynamicHeight();

  return (
    <Stack
      ref={ref}
      justifyContent="center"
      alignItems="center"
      gap={3}
      sx={{
        height,
        backgroundColor: pageColors.pageBackground,
        textAlign: "center",
        px: 2,
        overflow: "hidden",
      }}
      data-testid="empty-page-container"
    >
      {/* Icon */}
      <Stack justifyContent="center">
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            width: "178px",
            height: "178px",
            backgroundColor: neutral[0],
            borderRadius: "50%",
          }}
        >
          <SvgIcon
            inheritViewBox
            component={icon}
            sx={{
              height: ICON_SIZE,
              width: ICON_SIZE,
            }}
          />
        </Stack>
      </Stack>

      {/* Title */}
      <Stack
        justifyContent="center"
        sx={{
          fontSize: "24px",
          fontWeight: "600",
          color: pageColors.title,
          maxWidth: "90%",
          ...withEllipsisThreeLines,
          lineHeight: "2rem",
        }}
      >
        <SphereTooltip title={title}>{title}</SphereTooltip>
      </Stack>

      {/* Subtitle */}
      <Stack
        justifyContent="center"
        sx={{
          fontSize: "1rem",
          color: pageColors.subtitle,
          maxWidth: "90%",
          ...withEllipsisThreeLines,
        }}
      >
        <SphereTooltip title={subtitle}>{subtitle}</SphereTooltip>
      </Stack>

      {/* Button */}
      {button}
    </Stack>
  );
}
